import { useState, useEffect } from "react";
import { Lock, Loader2, CalendarCheck, Mail, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("bt_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [pass, setPass] = useState("");
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true);
    try {
      const [b, m] = await Promise.all([
        api.get("/bookings", { headers: { "X-Admin-Token": t } }),
        api.get("/messages", { headers: { "X-Admin-Token": t } }),
      ]);
      setBookings(b.data);
      setMessages(m.data);
      setAuthed(true);
    } catch {
      toast.error("Invalid access code.");
      localStorage.removeItem("bt_admin");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(token); }, []); // eslint-disable-line

  const login = async (e) => {
    e.preventDefault();
    localStorage.setItem("bt_admin", pass);
    setToken(pass);
    await load(pass);
  };

  const logout = () => { localStorage.removeItem("bt_admin"); setAuthed(false); setToken(""); setPass(""); };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink grain px-5">
        <form onSubmit={login} data-testid="admin-login" className="relative w-full max-w-sm rounded-sm border border-white/10 bg-white/[0.03] p-8">
          <div className="h-12 w-12 rounded-sm bg-tide/15 text-tide-bright flex items-center justify-center"><Lock size={22} /></div>
          <h1 className="mt-5 font-heading font-bold text-2xl text-white">Admin Dashboard</h1>
          <p className="mt-1 text-white/50 text-sm">Bright &amp; Tide Cleaning</p>
          <input data-testid="admin-passcode" type="password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Access code"
            className="mt-6 w-full rounded-sm border border-white/15 bg-white/5 px-4 py-3 text-white placeholder-white/30 outline-none focus:ring-2 focus:ring-tide" />
          <button data-testid="admin-login-btn" disabled={loading} className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-sm bg-tide text-white font-semibold py-3 hover:bg-tide-bright transition-colors disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  const items = tab === "bookings" ? bookings : messages;

  return (
    <div className="min-h-screen bg-secondary">
      <header className="bg-ink text-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="logo" className="h-10 w-10 rounded-full object-cover" />
            <div>
              <div className="font-heading font-bold">Admin Dashboard</div>
              <div className="text-xs text-white/50">Bright &amp; Tide Cleaning</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => load(token)} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white"><RefreshCw size={16} /> Refresh</button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <div className="flex gap-3">
          <button data-testid="admin-tab-bookings" onClick={() => setTab("bookings")} className={`inline-flex items-center gap-2 rounded-sm px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "bookings" ? "bg-ink text-white" : "bg-white border border-border text-ink/60"}`}>
            <CalendarCheck size={16} /> Bookings ({bookings.length})
          </button>
          <button data-testid="admin-tab-messages" onClick={() => setTab("messages")} className={`inline-flex items-center gap-2 rounded-sm px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "messages" ? "bg-ink text-white" : "bg-white border border-border text-ink/60"}`}>
            <Mail size={16} /> Messages ({messages.length})
          </button>
        </div>

        <div className="mt-6 space-y-3">
          {items.length === 0 && <div className="rounded-sm border border-border bg-white p-10 text-center text-ink/50">No {tab} yet.</div>}
          {tab === "bookings" && bookings.map((b) => (
            <div key={b.id} data-testid="admin-booking-row" className="rounded-sm border border-border bg-white p-5">
              <div className="flex flex-wrap justify-between gap-3">
                <div>
                  <div className="font-heading font-bold text-ink">{b.name} · <span className="text-tide">{b.service_type}</span></div>
                  <div className="text-sm text-ink/55 mt-1">{b.property_type} · {b.size} · {b.frequency}</div>
                </div>
                <div className="text-right text-sm">
                  <div className="font-semibold text-ink">{b.date} · {b.time}</div>
                  <div className="text-ink/50">{new Date(b.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-border text-sm text-ink/70 grid sm:grid-cols-2 gap-1">
                <span>📧 {b.email}</span><span>📞 {b.phone}</span>
                <span className="sm:col-span-2">📍 {b.address}</span>
                {b.notes && <span className="sm:col-span-2 text-ink/50">📝 {b.notes}</span>}
              </div>
            </div>
          ))}
          {tab === "messages" && messages.map((m) => (
            <div key={m.id} data-testid="admin-message-row" className="rounded-sm border border-border bg-white p-5">
              <div className="flex flex-wrap justify-between gap-3">
                <div className="font-heading font-bold text-ink">{m.name} {m.subject && <span className="text-ink/50 font-normal">· {m.subject}</span>}</div>
                <div className="text-sm text-ink/50">{new Date(m.created_at).toLocaleDateString()}</div>
              </div>
              <div className="text-sm text-ink/55 mt-1">{m.email} {m.phone && `· ${m.phone}`}</div>
              <p className="mt-3 pt-3 border-t border-border text-ink/75 whitespace-pre-wrap">{m.message}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
